<template>
  <div class="checkout-container">
    <div class="dashboard-tab">
      <h3 class="tab-title">订单确认</h3>
      
      <!-- 订单信息 -->
      <div class="order-info-section">
        <el-card class="order-card">
          <div class="order-header">
            <h4>订单信息</h4>
            <span class="order-no">订单号：{{ orderData.orderNo }}</span>
          </div>
          
          <div class="order-items">
            <div v-for="item in cartItems" :key="item.id" class="order-item">
              <span class="item-name">{{ item.itemName }}</span>
              <span class="item-quantity">×{{ item.quantity }}</span>
              <span class="item-price">¥{{ (item.unitPrice * item.quantity ).toFixed(2) }}</span>
            </div>
          </div>
    <!-- 重新购买提示 -->
    <el-alert
      v-if="showRenewNotice"
      title="重新购买提示"
      type="warning"
      :closable="false"
      style="margin-bottom: 20px;"
    >
      <p>您正在重新购买已取消的订单，价格信息保持原样。</p>
      <p>支付成功后，系统将为您开通相应服务。</p>
    </el-alert>
    
    <!-- 继续支付提示 -->
    <el-alert
      v-if="orderData.isContinuePayment"
      title="继续支付"
      type="info"
      :closable="false"
      style="margin-bottom: 20px;"
    >
      <p>您正在继续支付未完成的订单。</p>
      <p v-if="expireTime">请在 {{ expireTime }} 前完成支付</p>
    </el-alert>

          <div class="order-summary">
            <h3>{{ pageTitle }}</h3>          
            <div class="summary-row">
              <span>商品总额：</span>
              <span class="amount">¥{{ backendTotal.toFixed(2) }}</span>
            </div>
            <div class="summary-row">
              <span>优惠金额：</span>
              <span class="discount">-¥{{ backendDiscount.toFixed(2) }}</span>
            </div>
            <div class="summary-row total">
              <span>实付金额：</span>
              <span class="total-amount">¥{{ backendFinal.toFixed(2) }}</span>
            </div>
          </div>
        </el-card>
      </div>
      
      <!-- 支付方式选择 -->
      <div class="payment-section">
        <el-card class="payment-card">
          <h4>选择支付方式</h4>
          
          <div class="payment-methods">
            <div 
              class="payment-method" 
              :class="{ 'selected': selectedMethod === 'alipay' }"
              @click="selectPaymentMethod('alipay')"
            >
              <div class="method-icon">
                <i class="el-icon-bank-card"></i>
              </div>
              <div class="method-info">
                <span class="method-name">支付宝支付</span>
                <span class="method-desc">推荐使用支付宝扫码或账号支付</span>
              </div>
              <div class="method-radio">
                <i v-if="selectedMethod === 'alipay'" class="el-icon-circle-check"></i>
                <i v-else class="el-icon-circle-check-outline"></i>
              </div>
            </div>
            
            <div 
              class="payment-method" 
              :class="{ 'selected': selectedMethod === 'wechat' }"
              @click="selectPaymentMethod('wechat')"
            >
              <div class="method-icon">
                <i class="el-icon-chat-dot-round"></i>
              </div>
              <div class="method-info">
                <span class="method-name">微信支付</span>
                <span class="method-desc">打开手机微信扫码支付</span>
              </div>
              <div class="method-radio">
                <i v-if="selectedMethod === 'wechat'" class="el-icon-circle-check"></i>
                <i v-else class="el-icon-circle-check-outline"></i>
              </div>
            </div>
          </div>
          <a class="tips">套餐的新购、升级配置、续费需成功支付后，<b>重新登录</b>生效</a>
          <!-- 支付按钮 -->
          <div class="payment-actions">
            <el-button @click="$router.back()">返回修改</el-button>
            <el-button 
              type="primary" 
              :loading="loading"
              @click="handlePayment"
            >
                  {{ getPaymentButtonText() }}
            </el-button>
              <!-- 🔥 调试按钮 -->
    <el-button 
      v-if="debugMode"
        type="info" 
        @click="debugShowForm"
        :disabled="!alipayForm"
        style="margin-left: 10px;"
    >
        {{ showDebugForm ? '隐藏表单' : '查看表单HTML' }}
    </el-button>
          </div>
        </el-card>
      </div>
    </div>
    <!-- 调试区域 - 显示支付宝表单 -->
<el-card v-if="debugMode && showDebugForm" style="margin-top: 20px; border: 2px solid #e6a23c;">
    <div slot="header">
        <span style="font-weight: bold; color: #e6a23c;">🔧 调试：支付宝支付表单</span>
        <div style="float: right;">
            <el-button size="mini" type="success" @click="submitFormToAlipay" style="margin-right: 10px;">
                💰 提交支付（跳转支付宝）
            </el-button>
            <el-button size="mini" @click="showDebugForm = false">关闭</el-button>
        </div>
    </div>
    
    <!-- 显示表单信息 -->
    <div style="margin-bottom: 10px; padding: 10px; background: #f0f9eb; border-radius: 4px;">
        <p><strong>订单号：</strong>{{ orderData.orderNo || '未生成' }}</p>
        <p><strong>金额：</strong>¥{{ backendFinal || 0 }}</p>
        <p><strong>表单长度：</strong>{{ alipayForm?.length || 0 }} 字符</p>
    </div>
    
    <!-- 显示原始HTML -->
    <el-collapse>
        <el-collapse-item title="查看原始HTML" name="html">
            <pre style="max-height: 300px; overflow: auto; background: #f5f7fa; padding: 10px; border-radius: 4px; font-size: 12px; white-space: pre-wrap; word-break: break-all;">{{ alipayForm || '暂无表单数据' }}</pre>
        </el-collapse-item>
    </el-collapse>
    
    <!-- 显示渲染后的表单 -->
    <div style="margin-top: 10px; border: 1px solid #dcdfe6; padding: 15px; border-radius: 4px; background: white;">
        <div v-html="alipayForm" style="max-height: 400px; overflow: auto;"></div>
    </div>
</el-card>
    <!-- 支付宝表单（隐藏） -->
    <div  v-if="debugMode" v-html="alipayForm" style="border: 2px solid red; padding: 10px; margin: 10px;"></div>
  </div>
</template>

<script>
import { getCartItems, clearCart } from '@/api/tenant/cart'
import {  preCreateOrder,/* createRealOrder, confirmOrder, getCachedPreOrder ,*/createOrder, getOrderDetail } from '@/api/tenant/order'
import { http } from "../../api/base"

export default {
  name: 'CheckoutView',
  data() {
    return {
     pageTitle: '确认订单',
      showRenewNotice: false,      
      //====== 前端防重 机制 ==========
      isCreatingOrder: false, // 订单创建锁
      lastOrderTime: 0, // 上次创建订单时间
      currentOrderNo: null, // 当前处理中的订单      

       isPaying: false, 
      //=======================================
      selectedMethod: 'alipay',
      cartItems: [],
      orderData: {
        orderNo: '',
        totalAmount: 0,
        discountAmount: 0,
        finalAmount: 0,
        frontendTotal: 0.0,
        frontendDiscount: 0.0,
        frontendFinal: 0.0,
        preOrderNo: null,  // 预创建订单号（用于价格计算）
        formalOrderNo: null, // 正式订单号（用于支付）        
      },
      loading: false,
      alipayForm: '',

      showDebugForm: false,
      debugMode: false,
      wechatMsgBoxInstance: null,   // 🔥 微信支付弹框实例
       wechatPaymentHandled: false,   // 🔥 防止 onPaymentSuccess 被重复调用
        // 后端返回的价格信息：
        backendFinal: 0,  // 实付金额 
        backendTotal: 0,  // 商品总额
        backendDiscount: 0, // 优惠金额     
    }      
  },
  async mounted() {
     // 🔥 初始化调试模式
  this.debugMode = this.checkDebugMode();
  console.log('[checkout] debugMode =', this.debugMode);

  console.log("支付页面加载，参数:", this.$route.query);
    console.log("支付页面加载，参数:", this.$route.query);
      // 初始化支付方式
  this.selectedMethod = 'alipay'; // 默认支付宝
  
  // 如果有历史支付方式，可以恢复
  const savedMethod = localStorage.getItem('lastPaymentMethod');
  if (savedMethod && (savedMethod === 'alipay' || savedMethod === 'wechat')) {
    this.selectedMethod = savedMethod;
  }
    
    // 统一处理 orderNo 模式（继续支付和重新购买）
    if (this.$route.query.orderNo) {
      const orderNo = this.$route.query.orderNo;
      const from = this.$route.query.from || 'continue'; // 默认继续支付
      
      console.log(`订单号: ${orderNo}, 来源: ${from}`);
      
      if (from === 'renew') {
        await this.handleRenewOrder(orderNo);
      } else {
        await this.handleContinuePayment(orderNo);
      }
    } 
    // 购物车模式
    else if (this.$route.query.cartData) {
      console.log("购物车模式");
      await this.handleCartCheckout();
    }
    else {
      console.warn("无有效参数，返回订单列表");
      this.$router.push('/admin/order/list');
    }
  },
  beforeDestroy() {
    if (this.wechatMsgBoxInstance) {
      this.wechatMsgBoxInstance.close();
      this.wechatMsgBoxInstance = null;
    }
  },
  methods: {
    /**
 * 判断是否开启调试模式
 * 规则：
 *  1. 开发环境（VUE_APP_DEBUG_ENABLED=true）默认开启，无需任何参数
 *  2. 生产环境需要同时满足：
 *     - URL 带 ?debug=on
 *     - URL 带 ?token=xxx，且与 VUE_APP_DEBUG_TOKEN 匹配
 */
checkDebugMode() {
  // 1. 开发环境默认开启
  if (process.env.VUE_APP_DEBUG_ENABLED === 'true') {
    return true;
  }

  // 2. 生产环境：必须 debug=on
  const urlDebug = this.$route.query.debug === 'on';
  if (!urlDebug) return false;

  // 3. 生产环境：必须 token 匹配
  const urlToken = this.$route.query.token;
  const validToken = process.env.VUE_APP_DEBUG_TOKEN;
  if (!urlToken || !validToken || urlToken !== validToken) {
    console.warn('[checkout] debug token 校验失败');
    return false;
  }

  return true;
},
    // 获取支付按钮文字
    getPaymentButtonText() {
      if (this.orderData.isRenewOrder) {
        return '重新购买并支付';
      } else if (this.orderData.isContinuePayment) {
        return '继续支付';
      } else {
        return '立即支付';
      }
    },    
    // 处理支付（按支付方式分流）
    async handlePayment() {
      // 验证支付方式是否有效
      if (!this.selectedMethod || !['alipay', 'wechat'].includes(this.selectedMethod)) {
        this.$message.error('请选择有效的支付方式');
        return;
      }

      // 验证订单数据
      if (this.orderData.isRenewOrder || this.orderData.isContinuePayment) {
        if (!this.orderData.orderNo) {
          this.$message.error('订单号不存在');
          return;
        }
      } else {
        if (!this.cartItems || this.cartItems.length === 0) {
          this.$message.error('购物车为空');
          return;
        }
      }

      try {
        this.loading = true;

        console.log("=== 开始支付 ===");
        console.log("支付方式:", this.selectedMethod);

        // 统一入口：继续支付/重新购买 走 payExistingOrder，否则走 createAndPayNewOrder
        // 内部分流由这两个方法按 this.selectedMethod 处理（alipay / wechat）
        if (this.orderData.isRenewOrder || this.orderData.isContinuePayment) {
          console.log("支付已有订单");
          await this.payExistingOrder(this.orderData.orderNo);
        } else {
          console.log("创建新订单");
          await this.createAndPayNewOrder();
        }

        console.log("支付处理完成");

      } catch (error) {
        console.error('支付处理失败:', error);

        let errorMsg = '支付失败: ';
        if (error.response) {
          console.error('服务器响应:', error.response);
          errorMsg += error.response.data?.message || error.message;
        } else if (error.request) {
          console.error('无响应:', error.request);
          errorMsg += '服务器无响应，请检查网络连接';
        } else {
          errorMsg += error.message || '未知错误';
        }

        this.$message.error(errorMsg);

      } finally {
        this.loading = false;
      }
    },
    // 支付已有订单（按支付方式分流）
    async payExistingOrder(orderNo) {
      try {
        console.log("支付已有订单:", orderNo);

        console.log("=== 支付前数据检查 ===");
        console.log("1. cartItems:", this.cartItems);
        console.log("2. cartItems长度:", this.cartItems?.length);
        console.log("3. orderData:", this.orderData);

        if (!this.cartItems || this.cartItems.length === 0) {
          console.error("错误：购物车数据为空！");
          this.$message.error('商品数据加载失败，请刷新页面重试');
          return;
        }

        // 1. 获取订单详情
        const detailResponse = await getOrderDetail(orderNo);
        if (detailResponse.code !== 200) {
          throw new Error('获取订单详情失败');
        }

        const order = detailResponse.data;
        console.log("支付订单详情:", order);

        // 2. 构建购物车数据
        const cartItems = this.convertOrderItemsToCartItems(order.items || []);
        console.log("转换后的购物车数据:", cartItems);

        // 3. 构建 createOrder 请求数据
        const orderRequest = {
          cartItems: cartItems,
          payMethod: this.selectedMethod,
          subject: this.orderData.isRenewOrder ? '重新购买AI客服服务' : '继续支付订单',
          body: this.orderData.isRenewOrder ? '重新购买服务' : '继续支付'
        };

        console.log("createOrder请求数据:", orderRequest);
        console.log("API调用前 - 支付方式:", orderRequest.payMethod);

        // 4. 调用创建订单接口
        const response = await createOrder(orderRequest);
        console.log("createOrder", response);

        if (response.code !== 200) {
          throw new Error(response.message || '创建订单失败');
        }

        // 🔥 5. 按支付方式分流
        if (this.selectedMethod === 'wechat') {
          // 微信支付：response.data 是支付数据
          const paymentData = response.data || {};
          const newOrderNo = paymentData.orderNo || this.orderData.orderNo || orderNo;
          if (newOrderNo) this.orderData.orderNo = newOrderNo;

          console.log("微信支付数据:", paymentData);
          await this.handleWechatPayment(newOrderNo, paymentData);
        } else {
          // 支付宝：response.message 是表单 HTML
          const payFormHtml = response.message;

          const parsedOrderNo = this.extractOrderNoFromForm(payFormHtml);
          if (parsedOrderNo) {
            console.log("解析到的订单号：", parsedOrderNo);
            this.orderData.orderNo = parsedOrderNo;
          }

          this.submitToPay(payFormHtml);
        }

      } catch (error) {
        console.error('支付已有订单失败:', error);
        throw error;
      }
    },
      // 提交支付表单
      submitToPay(paymentForm) {
      console.log("=== submitToPay 被调用 ===");
      console.log("paymentForm 类型:", typeof paymentForm);
      console.log("paymentForm 长度:", paymentForm?.length || 0);
      
      // 🔥 1. 保存到 alipayForm（让红框显示）
      this.alipayForm = paymentForm;
      console.log("✅ alipayForm 已保存，长度:", this.alipayForm?.length || 0);
      
      // 2. 提取订单号
      if (paymentForm && typeof paymentForm === 'string') {
          const orderNoMatch = paymentForm.match(/out_trade_no["']?\s*[:=]\s*["']?([^"',}\s]+)/);
          if (orderNoMatch) {
              this.orderData.orderNo = orderNoMatch[1];
              console.log("✅ 订单号:", orderNoMatch[1]);
          }
          
          // 3. 提取金额
          const amountMatch = paymentForm.match(/total_amount["']?\s*[:=]\s*["']?([^"',}\s]+)/);
          if (amountMatch) {
              console.log("✅ 金额:", amountMatch[1]);
          }
      }
      if (this.debugMode) {
      // 调试模式：只显示表单，手动提交
      this.showDebugForm = true;
      this.$message.success('【调试模式】支付表单已生成，请检查后手动提交');
    } else {
      // 生产模式：直接自动提交跳转支付宝
      this.$nextTick(() => {
        const div = document.createElement('div');
        div.innerHTML = paymentForm;
        document.body.appendChild(div);
        const form = div.querySelector('form');
        if (form) {
          form.submit();
        } else {
          this.$message.error('支付表单加载失败');
        }
      }); 
      }
      },  
      // 创建新订单并支付（按支付方式分流）
      async createAndPayNewOrder() {
        try {
          console.log("创建新订单并支付");

          // 1. 验证购物车数据
          if (!this.cartItems || this.cartItems.length === 0) {
            this.$message.error('购物车为空，无法支付');
            return;
          }

          // 2. 准备购物车数据
          const preCreateItems = this.cartItems.map(item => ({
            itemId: item.itemId || item.id,
            itemType: item.itemType,
            quantity: item.quantity || 1
          }));

          console.log("预创建购物车数据:", preCreateItems);

          // 3. 调用预创建接口（如果还没调用过）
          if (!this.orderData.tempOrderNo) {
            const preCreateResponse = await preCreateOrder({ items: preCreateItems });
            console.log("预创建响应:", preCreateResponse);

            if (preCreateResponse.data.code !== 200) {
              this.$message.error(preCreateResponse.data.message || '预创建失败');
              return;
            }

            const result = preCreateResponse.data || preCreateResponse;
            this.orderData.tempOrderNo = result.temp_order_no;
            this.orderData.originalTotal = result.original_total;
            this.orderData.discountAmount = result.discount_amount;
            this.orderData.payableAmount = result.payable_amount;
          }

          // 4. 构建创建订单的请求数据
          const orderRequest = {
            cartItems: preCreateItems,
            payMethod: this.selectedMethod,
            subject: 'AI客服服务',
            body: '购买AI客服服务'
          };

          console.log("创建订单请求:", orderRequest);

          // 5. 调用创建订单接口
          const response = await createOrder(orderRequest);
          console.log("创建订单响应:", response);

          if (response.code !== 200) {
            this.$message.error(response.message || '创建订单失败');
            return;
          }

          // 🔥 6. 按支付方式分流
          if (this.selectedMethod === 'wechat') {
            // 微信支付：response.data 是支付数据（codeUrl / payUrl / tradeType 等）
            const paymentData = response.data || {};
            const orderNo = paymentData.orderNo || this.orderData.orderNo || '临时订单号';
            if (orderNo) this.orderData.orderNo = orderNo;

            console.log("微信支付数据:", paymentData);
            await this.handleWechatPayment(orderNo, paymentData);
          } else {
            // 支付宝：response.message 是表单 HTML
            this.submitToPay(response.message);
          }

        } catch (error) {
          console.error('创建新订单失败:', error);
          throw error;
        }
      },
    
        async createPreOrder(cartItems) {
          console.log("createPreOrder1",cartItems)
          try {
            const response = await preCreateOrder(cartItems);
            console.log("createPreOrder2",response);
            if (response.data.data.latest_order_no && response.data.has_pending_order) {
              // 提示用户有未完成订单
              this.$confirm(
                `您有未支付的订单（${response.data.latest_order_no}），是否继续支付？`,
                '提示',
                {
                  confirmButtonText: '继续支付',
                  cancelButtonText: '取消并创建新订单',
                  type: 'warning'
                }
              ).then(() => {
                // 跳转到未支付订单
                this.$router.push(`/order/pay/${response.data.latest_order_no}`);
              }).catch(() => {
                // 继续创建新订单
                this.processNewOrder(response.data);
              });
            } else {
              // 正常处理新订单
               this.processNewOrder(response.data);
            }
            return response;
          } catch (error) {
            if (error.message.includes('未支付的订单')) {
              // 提取订单号，提供快捷操作
              const orderNoMatch = error.message.match(/订单号：(\w+)/);
              if (orderNoMatch) {
                this.showUnpaidOrderDialog(orderNoMatch[1]);
              }
            }
            this.$message.error(error.message || '创建订单失败');
          }
        }, 
        /**
         * 从响应中提取订单号
         */
        extractOrderNo(response) {
          // 尝试多种方式提取订单号
          if (response.data && response.data.orderNo) {
            return response.data.orderNo;
          }
          
          // 从支付宝表单中提取（如果有）
          if (response.message && typeof response.message === 'string') {
            const orderNoMatch = response.message.match(/out_trade_no['"]\s*:\s*['"](\w+)['"]/);
            if (orderNoMatch) {
              return orderNoMatch[1];
            }
          }
          
          // 如果没有明确的订单号，生成一个临时标识
          return `TEMP_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        },           
        /**
         * 处理新订单（简洁版）
         */
        processNewOrder(orderData) {
          console.log("processNewOrder",orderData);
          try {
            // 1. 保存订单数据到本地状态
            this.orderResult = orderData;
            this.tempOrderNo = orderData.data.temp_order_no;
                        console.log(" this.tempOrderNo", this.tempOrderNo);
            // 2. 更新价格显示
            console.log("orderData.data", orderData.data)
            this.backendTotal = orderData.data.original_total ||0;
            this.backendDiscount = orderData.data.discount_amount||0;
            this.backendFinal = orderData.data.payable_amount||0;
                        console.log(" this.backendFinal", this.backendFinal);            
            // 3. 检查价格差异
            const frontendFinal = parseFloat(this.frontendFinal) || 0;
            const backendFinal = parseFloat(this.backendFinal) || 0;
            const diff = Math.abs(frontendFinal - backendFinal);
            
            if (diff > 0.01) {
              this.$notify({
                title: '价格已更新',
                message: `实际金额：¥${this.backendFinal}`,
                type: 'info',
                duration: 3000
              });
            }
            
            // 4. 启用支付按钮
            this.priceValid = true;
            
          } catch (error) {
            console.error('处理新订单失败:', error);
            this.$message.error('订单数据处理异常');
          }
        },
        async loadCartData() {
          try {
            const response = await getCartItems()
            this.cartItems = response.data.data
            console.log(this.cartItems);
            // 计算金额（注意：后端存储的是分，前端需要转换）
            this.orderData.totalAmount = this.cartItems.reduce((sum, item) => 
              sum + (item.unitPrice * item.quantity), 0)
            console.log(this.orderData)
            // 这里应该调用后端接口获取实际优惠金额
            // 暂时设为0
            this.orderData.discountAmount = 0
            this.orderData.finalAmount = this.orderData.totalAmount - this.orderData.discountAmount
          } catch (error) {
            console.error('加载购物车数据失败:', error)
            this.$message.error('加载订单信息失败')
          }
        },    
        generateOrderNo() {
          // 生成临时订单号（实际应由后端生成）
          const timestamp = new Date().getTime()
          const random = Math.floor(Math.random() * 1000)
          this.orderData.orderNo = `DD${timestamp}${random}`
        },
        async createOrderAndGetRealPrice() {
            try {
                // 调用统一接口
                const response = await this.createPreOrder({
                    items: this.cartItems.map(item => ({
                        itemId: item.itemId,
                        itemType: item.itemType,
                        quantity: item.quantity
                    }))
                });
                
                // 处理统一格式的结果
                console.log(response);
                if (response.data && response.data.data) {
                    const result = response.data.data;
                    this.processUnifiedPriceResult(result);
                }
                
            } catch (error) {
                console.error('获取订单价格失败:', error);
            }
        },
        // 新增：调试预创建
        async debugPreCreate() {
          console.log("=== 预创建调试 ===");
          
          // 测试1：检查cartItems格式
          const testItems = (this.cartItems || []).map(item => ({
            itemId: item.itemId || item.id,
            itemType: item.itemType,
            quantity: item.quantity || 1
          }));
          
          console.log("测试1 - 转换后的商品数据:", testItems);
          
          if (testItems.length === 0) {
            console.error("错误：购物车为空");
            this.$message.error('购物车为空');
            return;
          }
          
          // 测试2：直接调用preCreateOrder
          try {
            console.log("测试2 - 调用preCreateOrder接口");
            const response = await preCreateOrder({ items: testItems });
            console.log("预创建接口响应:", response);
            
            if (response && response.data.code === 200) {
              console.log("✅ 预创建成功！", response.data);
              this.$message.success('预创建成功');
            } else {
              console.error("❌ 预创建失败:", response?.data.message);
              this.$message.error('预创建失败: ' + (response?.data.message || '未知错误'));
            }
          } catch (error) {
            console.error("❌ 预创建异常:", error);
            this.$message.error('预创建异常: ' + error.message);
          }
        },
        processUnifiedPriceResult(result) {
            // 标准化字段名
            const normalized = this.normalizePriceResult(result);
            
            // 更新价格显示
            this.backendTotal = normalized.original_total || normalized.originalTotal || 0;
            this.backendDiscount = normalized.discount_amount || normalized.discountAmount || 0;
            this.backendFinal = normalized.payable_amount || normalized.payableAmount || 
                              normalized.final_amount || normalized.finalAmount || 0;
            
            // 保存商品详情
            this.orderItemDetails = normalized.item_details || normalized.itemDetails || [];
            
            console.log("统一价格计算结果（标准化后）:", normalized);
        },

        normalizePriceResult(result) {
            // 简单标准化，优先使用下划线，其次驼峰
            return {
                original_total: result.original_total || result.originalTotal,
                discount_amount: result.discount_amount || result.discountAmount,
                payable_amount: result.payable_amount || result.payableAmount,
                final_amount: result.final_amount || result.finalAmount,
                item_details: result.item_details || result.itemDetails,
                applied_promotions: result.applied_promotions || result.appliedPromotions
            };
        },
        async createFormalOrder() {
          // 防重复点击：10秒内只能创建一次订单
          const now = Date.now();
          if (this.isCreatingOrder || (now - this.lastOrderTime < 10000)) {
            throw new Error('请勿重复提交，订单正在处理中');
          }
          
          this.isCreatingOrder = true;

          this.lastOrderTime = now;          
          try {
                // 检查是否已有未支付订单(查重)
                const hasUnpaid = await this.checkUnpaidOrder();
                console.log("checkUnpaidOrder:",hasUnpaid)
                if (hasUnpaid) {
                  throw new Error('您有未支付的订单，请先完成支付');
                }
                console.log("=== createFormalOrder 开始 ===");
                console.log("购物车数据:", this.cartItems);
                
                // 构造请求数据
                const requestData = {
                  subject: '小晖智能客服 - 服务套餐',
                  body: `包含${this.cartItems.length}个商品`,
                  payMethod:  this.selectedMethod,
                  cartItems: this.cartItems.map(item => ({
                    itemId: item.itemId,
                    itemType: item.itemType,
                    quantity: item.quantity,
                    itemName: item.itemName,
                    unitPrice: item.unitPrice
                  }))
                };
            
               console.log("发送订单创建请求:", requestData);
            
            // 调用后端创建订单接口
            const response = await createOrder(requestData);
            
            console.log("后端响应完整结构:");
            console.log("- response 对象:", response);
            console.log("- response.code:", response.code);
            console.log("- response.message 类型:", typeof response.message);
            console.log("- response.data:", response.data);
            
            // 检查响应是否包含支付宝表单
            if (response.message && typeof response.message === 'string') {
              console.log("- response.message 前100字符:", 
                          response.message.substring(0, 100));
              console.log("- 是否包含 alipay:", response.message.includes('alipay'));
              console.log("- 是否包含 out_trade_no:", response.message.includes('out_trade_no'));
            }
            
            if (response.code !== 200) {
              throw new Error(response.message || '创建订单失败');
            }
            
            let orderNo = null;
            
            // 方案1：从支付宝表单中提取订单号
            if (response.message && typeof response.message === 'string') {
              // 正则表达式匹配订单号
              const match1 = response.message.match(/out_trade_no['"]?\s*[:=]\s*['"]?(\w+)['"]?/);
              const match2 = response.message.match(/["']out_trade_no["']\s*:\s*["']([^"']+)["']/);
              
              if (match1) {
                orderNo = match1[1];
                console.log("从正则1提取订单号:", orderNo);
              } else if (match2) {
                orderNo = match2[1];
                console.log("从正则2提取订单号:", orderNo);
              }
              
              // 保存支付宝表单
              this.alipayForm = response.message;
              console.log("支付宝表单已保存，长度:", this.alipayForm.length);
            }
            
            // 方案2：从响应数据中获取订单号
            if (!orderNo && response.data) {
              // 尝试不同的字段名
              orderNo = response.data.orderNo || 
                        response.data.out_trade_no || 
                        response.data.order_no;
              console.log("从response.data获取订单号:", orderNo);
            }
            
            // 方案3：生成临时订单号
            if (!orderNo) {
              orderNo = `ORDER_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
              console.log("生成临时订单号:", orderNo);
            }
            
            // 保存到本地状态
            this.orderData.orderNo = orderNo;
            console.log("createFormalOrder 完成，订单号:", orderNo);
            
            return orderNo;
            
          } catch (error) {
            console.error("创建正式订单失败:", error);
            throw error;
          } finally {
            this.isCreatingOrder = false;
          }

        },        
        // 检查未支付订单
      async checkUnpaidOrder() {
        try {
          const response = await this.$http.get('/aics/order/unpaid');
          return response.data;
        } catch (error) {
          console.warn('检查未支付订单失败:', error);
          return false;
        }
      },
        checkPriceConsistency() {
          const diff = Math.abs(this.frontendFinal - this.backendFinal);
          const threshold = 0.01; // 1分钱误差内可接受
          
          if (diff > threshold) {
            // 价格不一致，提示用户
            this.showPriceUpdateNotice();
            // 以后端价格为准
            this.finalAmount = this.backendFinal;
          } else {
            // 价格一致，使用后端价格
            this.finalAmount = this.backendFinal;
          }
        },
        showPriceUpdateNotice() { 
            this.$notify({
                  title: '价格已更新',
        
                  message: `系统已重新计算优惠，最终金额为¥${this.backendTotal.toFixed(2)}`,
                  type: 'info',
                  duration: 5000
                });          
        },
        selectPaymentMethod(method) {
          console.log("用户选择了支付方式:", method);
          this.selectedMethod = method;
          // 保存到本地存储，下次恢复
          localStorage.setItem('lastPaymentMethod', method);
        },

        async payWithAlipay() {
          if (this.isPaying) {
            this.$message.warning('正在支付中，请勿重复点击');
            return;
          }
          
          this.isPaying = true;
          this.loading = true;
          
          try {
            console.log("=== payWithAlipay 开始 ===");
            
            // 1. 创建正式订单（这个方法会返回支付宝表单）
            const orderNo = await this.createFormalOrder();
            console.log("订单创建成功，订单号:", orderNo);
            
            // 2. 检查支付宝表单
            if (!this.alipayForm || this.alipayForm.trim().length === 0) {
              throw new Error('未获取到支付宝支付表单');
            }
            
            console.log("支付宝表单已准备，长度:", this.alipayForm.length);
            
            // 3. 直接提交支付宝表单（不需要再次调用 createOrder）
            setTimeout(() => {
              console.log("开始提交支付宝表单...");
              
              const tempDiv = document.createElement('div');
              tempDiv.innerHTML = this.alipayForm;
              document.body.appendChild(tempDiv);
              
              const form = tempDiv.querySelector('form');
              if (form) {
                console.log("找到表单，自动提交...");
                form.submit();
              } else {
                console.error("未找到表单元素");
                this.$message.error('支付表单加载失败');
              }
            }, 100);
            
            // 注意：这里不需要返回任何东西，表单提交后会跳转到支付宝
            
          } catch (error) {
            console.error('支付宝支付失败:', error);
            this.$message.error(`支付失败: ${error.message}`);
          } finally {
            this.isPaying = false;          
             this.loading = false;
          }
        },


        /**
         * 调用微信支付API
         */
        async callWechatPayApi(paymentRequest) {
          try {
            // 调用微信支付接口
            // 例如：POST /api/payment/wechat/create
            const response = await this.$http.post('/api/payment/wechat/create', paymentRequest);
            return response.data;
          } catch (error) {
            console.error('调用微信支付接口失败:', error);
            throw error;
          }
        },

          /**
           * 处理微信支付响应
           */
          async handleWechatResponse(wechatData) {
            // 根据返回的数据类型处理
            if (wechatData.codeUrl) {
              // 有二维码URL，显示二维码
              this.showWechatQRCode(wechatData.codeUrl, wechatData.orderNo);
              
            } else if (wechatData.jsApiParams) {
              // 公众号支付，调起微信JSAPI
              this.invokeWechatJSAPI(wechatData.jsApiParams);
              
            } else if (wechatData.h5Url) {
              // H5支付，跳转URL
              window.location.href = wechatData.h5Url;
              
            } else {
              throw new Error('不支持的微信支付方式');
            }
          },
          /**
           * 显示微信支付二维码
           */
          showWechatQRCode(codeUrl, orderNo) {
//            const that = this;   // 🔥 关键：先声明 that，后面 callback / beforeClose 里都用它
            const qrImgUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(codeUrl)}`;

            this.$msgbox({
              title: '微信支付',
              message: `
                <div style="text-align:center;">
                  <p>请使用微信扫描二维码支付</p>

                  <div id="wechat-qr-wrapper" style="position:relative;width:200px;height:200px;margin:10px auto;">
                    <div id="wechat-qr-loading"
                        style="position:absolute;top:0;left:0;width:100%;height:100%;
                                display:flex;align-items:center;justify-content:center;
                                background:#f5f7fa;border-radius:4px;color:#909399;font-size:13px;">
                      <i class="el-icon-loading" style="font-size:24px;margin-right:6px;"></i>
                      二维码生成中...
                    </div>
                    <img id="wechat-qr-img"
                        src="${qrImgUrl}"
                        alt="微信支付二维码"
                        style="width:200px;height:200px;display:none;" />
                  </div>

                  <p style="color:#999;font-size:12px;">订单号: ${orderNo}</p>
                  <p style="color:#f56c6c;margin-top:10px;">支付成功后页面会自动跳转</p>
                  <div style="margin-top:10px;">
                    <a href="weixin://" style="color:#409eff;text-decoration:none;">
                      <i class="el-icon-mobile-phone"></i> 打开微信
                    </a>
                  </div>
                </div>
              `,
         showCancelButton: true,
    confirmButtonText: '我已支付',
    cancelButtonText: '取消支付',
    dangerouslyUseHTMLString: true,

    callback: (action, instance) => {
      if (!this.wechatMsgBoxInstance) {
        this.wechatMsgBoxInstance = instance;
        console.log('[wechat-qr] 保存弹框实例');
      }
    },

    beforeClose: async (action, instance, done) => {
      if (action === 'confirm') {
        const paid = await this.checkWechatPaymentStatus(orderNo);
        if (paid) {
          done();   // 先关弹框
          // 用 handlePaymentSuccessOnce 统一处理，带去重
          await this.handlePaymentSuccessOnce(orderNo);
        } else {
          this.$message.warning('支付未完成，请确认支付状态');
          return false;
        }
      } else {
        this.$message.info('支付已取消');
        done();
      }
    }
  });

  // 二维码渲染（保持你现有逻辑）
  this.$nextTick(() => {
    setTimeout(() => {
      const img = document.getElementById('wechat-qr-img');
      const loading = document.getElementById('wechat-qr-loading');
      if (!img || !loading) return;
      if (img.complete && img.naturalWidth > 0) {
        loading.style.display = 'none';
        img.style.display = 'block';
        return;
      }
      img.addEventListener('load', () => {
        loading.style.display = 'none';
        img.style.display = 'block';
      });
      img.addEventListener('error', () => {
        loading.innerHTML = '二维码加载失败，请刷新重试';
        loading.style.color = '#f56c6c';
      });
    }, 50);
  });

  this.startWechatPaymentPolling(orderNo);
},
          /**
           * 调用支付宝API（可根据实际情况调整）
           */
          async callAlipayApi(orderRequest) {
            // 方案1：直接使用现有的 createOrder 接口（如果它返回支付宝表单）
            if (orderRequest.orderNo) {
              // 如果已经有了订单号，调用单独的支付宝支付接口
              return await this.createAlipayPayment(orderRequest);
            } else {
              // 如果 createOrder 已经包含了支付宝支付，直接使用
              return await createOrder(orderRequest);
            }
          },

        /**
         * 轮询支付状态
         */
        startPaymentPolling(orderNo) {
          const pollInterval = setInterval(async () => {
            try {
              const paid = await this.checkPaymentStatus(orderNo);
              if (paid) {
                clearInterval(pollInterval);
                await this.onPaymentSuccess(orderNo);
                this.$message.success('支付成功！');
              }
            } catch (error) {
              console.error('轮询支付状态失败:', error);
            }
          }, 3000); // 每3秒检查一次
          
          // 10分钟后停止轮询
          setTimeout(() => {
            clearInterval(pollInterval);
          }, 600000);
        },

          /**
           * 检查支付状态
           */
            async checkPaymentStatus(orderNo) {
              try {
                const response = await http.get(`/api/order/payment-status/${orderNo}`);
                return response.data.data === 'PAID';
              } catch (error) {
                console.error('检查支付状态失败:', error);
                return false;
              }
            },

/**
 * 强制提交支付宝表单
 */
forceSubmitAlipayForm(htmlContent) {
  // 创建一个临时div来渲染表单
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = htmlContent;
  document.body.appendChild(tempDiv);
  
  // 找到表单
  const form = tempDiv.querySelector('form[name="punchout_form"]');
  if (form) {
    console.log('找到表单，开始提交...');
    form.submit();
  } else {
    console.error('未找到支付表单');
    this.$message.error('支付表单加载失败');
    
    // 尝试手动创建表单
    this.createManualAlipayForm(htmlContent);
  }
},

/**
 * 手动创建支付宝表单（备选方案）
 */
createManualAlipayForm(htmlContent) {
  try {
    // 尝试从HTML中提取关键参数
    const params = {};
    const inputRegex = /<input[^>]*name="([^"]*)"[^>]*value="([^"]*)"[^>]*>/g;
    let match;
    
    while ((match = inputRegex.exec(htmlContent)) !== null) {
      params[match[1]] = match[2];
    }
    
    console.log('提取的表单参数:', params);
    
    // 如果有必要的参数，手动创建表单
    if (params.biz_content || params.out_trade_no) {
      this.$message.info('正在跳转到支付宝...');
      
      // 这里需要根据实际支付宝接口创建表单
      // 由于安全原因，通常应该由后端返回完整的表单
      
    } else {
      this.$message.error('无法提取支付参数');
    }
  } catch (error) {
    console.error('手动创建表单失败:', error);
  }
},
   /**
     * 提交表单到支付宝
     */
    submitFormToAlipay() {
        if (!this.alipayForm) {
            this.$message.warning('没有表单可提交，请先点击"立即支付"生成表单');
            return;
        }
        
        console.log("=== 手动提交表单到支付宝 ===");
        
        // 检查表单内容
        if (!this.alipayForm.includes('<form')) {
            this.$message.error('表单格式错误，请重新生成');
            return;
        }
        
        // 提取订单号
        const orderNoMatch = this.alipayForm.match(/out_trade_no["']?\s*[:=]\s*["']?([^"',}\s]+)/);
        if (orderNoMatch) {
            console.log("提交订单号:", orderNoMatch[1]);
        }
        
        // 显示确认对话框
        this.$confirm('确认提交支付表单，跳转到支付宝支付页面？', '确认支付', {
            confirmButtonText: '确认支付',
            cancelButtonText: '取消',
            type: 'warning'
        }).then(() => {
            // 创建临时容器
            const div = document.createElement('div');
            div.innerHTML = this.alipayForm;
            document.body.appendChild(div);
            
            // 查找表单
            const form = div.querySelector('form[name="punchout_form"]');
            if (form) {
                console.log("✅ 找到表单，提交到支付宝");
                
                // 可选：在新窗口打开
                // form.target = '_blank';
                
                // 提交表单
                form.submit();
                
                // 清理临时DOM（页面跳转后可能不会执行）
                setTimeout(() => {
                    if (document.body.contains(div)) {
                        document.body.removeChild(div);
                    }
                }, 1000);
            } else {
                // 尝试查找任何表单
                const anyForm = div.querySelector('form');
                if (anyForm) {
                    console.log("找到备用表单，提交");
                    anyForm.submit();
                } else {
                    this.$message.error('未找到支付表单');
                }
            }
        }).catch(() => {
            console.log("用户取消支付");
        });
    },
    
debugShowForm() {
        if (!this.alipayForm) {
            this.$message.warning('请先点击支付按钮生成表单');
            return;
        }
        this.showDebugForm = !this.showDebugForm;
        console.log('alipayForm:', this.alipayForm);
    },
    
    submitDebugForm() {
        if (!this.alipayForm) {
            this.$message.warning('没有表单可提交');
            return;
        }
        const div = document.createElement('div');
        div.innerHTML = this.alipayForm;
        document.body.appendChild(div);
        const form = div.querySelector('form');
        if (form) {
            console.log('手动提交表单');
            form.submit();
        } else {
            this.$message.error('未找到表单');
        }
    },

   /**
     * 计算前端总价（用于预展示）
     * 包含商品总额、优惠估算、实付金额
     */
    calculateFrontendTotal() {
      try {
        // 策略3：简单估算（最简方案）
        return this.calculateSimpleEstimation();
        
      } catch (error) {
        console.error('前端价格计算失败:', error);
        // 降级方案：只计算商品总价
        return this.calculateBasicTotalOnly();
      }
    },
    
    /**
     * 策略1：使用购物车已有的优惠计算结果
     */
    calculateFromCartDiscount() {
      console.log('使用购物车优惠计算结果');
      
      // 商品总价（直接从购物车数据计算）
      const total = this.cartItems.reduce((sum, item) => {
        const unitPrice = item.unitPrice || 0;
        const quantity = item.quantity || 1;
        return sum + (unitPrice * quantity);
      }, 0);
      
      // 如果有购物车计算好的优惠金额，直接使用
      let discount = 0;
      let discountDetails = [];
      
      if (this.promotions_used && this.promotions_used.length > 0) {
        // 使用购物车页面的优惠计算结果
        discount = this.promotions_used.reduce((sum, promo) => {
          return sum + (promo.discount || 0);
        }, 0);
        
        discountDetails = this.promotions_used.map(promo => ({
          name: promo.activityName,
          amount: promo.discount,
          type: promo.activityType
        }));
      } else if (this.total_discount > 0) {
        // 使用直接计算的优惠总额
        discount = this.total_discount;
      }
      
      // 计算实付金额
      const final = Math.max(0, total - discount);
      
      return {
        total: this.formatPriceYuan(total),
        discount: this.formatPriceYuan(discount),
        final: this.formatPriceYuan(final),
        discountDetails,
        calculationMethod: 'cart',
        accuracy: 'high'
      };
    },
    
    /**
     * 策略2：调用本地优惠计算规则
     */
    calculateWithLocalRules() {
      console.log('使用本地优惠规则计算');
      
      // 加载本地优惠规则（可以从配置文件或常量加载）
      const localPromotions = this.getLocalPromotionRules();
      
      // 计算商品总价（分转元）
      const totalYuan = this.cartItems.reduce((sum, item) => {
        const unitPriceCents = item.unitPrice || 0;
        const unitPriceYuan = unitPriceCents / 100;
        const quantity = item.quantity || 1;
        return sum + (unitPriceYuan * quantity);
      }, 0);
      
      // 应用本地优惠规则
      let discountYuan = 0;
      const discountDetails = [];
      
      for (const promotion of localPromotions) {
        const applicable = this.checkPromotionApplicable(promotion, totalYuan);
        if (applicable) {
          const discount = this.calculatePromotionDiscount(promotion, totalYuan);
          if (discount > 0) {
            discountYuan += discount;
            discountDetails.push({
              name: promotion.name,
              amount: discount,
              type: promotion.type,
              description: promotion.description
            });
          }
        }
      }
      
      // 确保优惠不超过总价
      discountYuan = Math.min(discountYuan, totalYuan);
      const finalYuan = Math.max(0, totalYuan - discountYuan);
      
      return {
        total: this.formatPrice(totalYuan),
        discount: this.formatPrice(discountYuan),
        final: this.formatPrice(finalYuan),
        discountDetails,
        calculationMethod: 'local_rules',
        accuracy: 'medium'
      };
    },
    
    /**
     * 策略3：简单估算（无优惠规则时使用）
     */
    calculateSimpleEstimation() {
      console.log('使用简单估算');
      
      // 商品总价
      const total = this.cartItems.reduce((sum, item) => {
        const price = item.unitPrice || 0;
        const quantity = item.quantity || 1;
        return sum + (price * quantity);
      }, 0);
      
      // 简单折扣估算（如果有历史数据或默认规则）
      let discount = 0;
      
      // 方案A：固定比例估算（如95折）
      const defaultDiscountRate = this.getDefaultDiscountRate();
      if (defaultDiscountRate > 0) {
        discount = total * defaultDiscountRate;
      }
      
      // 方案B：根据商品类型估算
      const typeBasedDiscount = this.estimateDiscountByItemType();
      discount = Math.max(discount, typeBasedDiscount);
      
      // 限制最大折扣（不超过总价的50%）
      const maxDiscount = total * 0.5;
      discount = Math.min(discount, maxDiscount);
      
      // 方案C：如果总价较高，给予阶梯优惠
      if (total > 100000) { // 1000元以上
        discount = Math.max(discount, total * 0.1); // 至少9折
      }
      
      const final = Math.max(0, total - discount);
      
      return {
        total: this.formatPriceYuan(total),
        discount: this.formatPriceYuan(discount),
        final: this.formatPriceYuan(final),
        discountDetails: [{
          name: '估算优惠',
          amount: discount,
          type: 'estimate'
        }],
        calculationMethod: 'estimate',
        accuracy: 'low',
        note: '此为估算价格，实际价格以后端计算为准'
      };
    },
    
    /**
     * 降级方案：只计算基础总价
     */
    calculateBasicTotalOnly() {
      console.log('使用基础计算（降级方案）');
      
      const total = this.cartItems.reduce((sum, item) => {
        const price = item.unitPrice || 0;
        const quantity = item.quantity || 1;
        return sum + (price * quantity);
      }, 0);
      
      return {
        total: this.formatPriceYuan(total),
        discount: '0.00',
        final: this.formatPriceYuan(total),
        discountDetails: [],
        calculationMethod: 'basic',
        accuracy: 'high',
        note: '仅计算商品总价，优惠需等待后端计算'
      };
    },
    
    // ========== 辅助方法 ==========
    
    /**
     * 检查是否可以使用购物车的优惠计算结果
     */
    canUseCartDiscount() {
      return this.cartItems && this.cartItems.length > 0 && 
             (this.promotions_used || this.total_discount !== undefined);
    },
    
    /**
     * 是否有本地优惠规则
     */
    hasLocalPromotionRules() {
      // 检查是否有缓存的优惠规则或配置
      const rules = this.getLocalPromotionRules();
      return rules && rules.length > 0;
    },
    
    /**
     * 获取本地优惠规则（可以从localStorage或配置文件加载）
     */
    getLocalPromotionRules() {
      try {
        // 方案A：从localStorage加载缓存规则
        const cachedRules = localStorage.getItem('local_promotion_rules');
        if (cachedRules) {
          return JSON.parse(cachedRules);
        }
        
        // 方案B：使用内置默认规则
        return this.getDefaultPromotionRules();
        
      } catch (error) {
        console.warn('加载本地优惠规则失败:', error);
        return [];
      }
    },
    
    /**
     * 默认优惠规则
     */
    getDefaultPromotionRules() {
      return [
        {
          id: 1,
          name: '新用户首单优惠',
          type: 'discount',
          rate: 0.1, // 9折
          condition: 'first_order',
          description: '新用户专享9折优惠'
        },
        {
          id: 2,
          name: '满100减10',
          type: 'full_reduction',
          threshold: 100,
          reduce: 10,
          description: '满100元减10元'
        },
        {
          id: 3,
          name: '套餐专项优惠',
          type: 'item_based',
          itemTypes: [1], // 套餐
          rate: 0.05, // 95折
          description: '套餐类商品95折'
        }
      ];
    },
    
    /**
     * 检查优惠规则是否适用
     */
    checkPromotionApplicable(promotion, totalAmount) {
      switch (promotion.condition) {
        case 'first_order':
          // 检查是否为新用户（这里需要实际业务逻辑）
          return this.isNewUser();
          
        case 'no_condition':
          return true;
          
        default:
          // 检查金额门槛
          if (promotion.threshold && totalAmount >= promotion.threshold) {
            return true;
          }
          return false;
      }
    },
    
    /**
     * 计算单个优惠的折扣金额
     */
    calculatePromotionDiscount(promotion, totalAmount) {
      switch (promotion.type) {
        case 'discount':
          if (promotion.rate) {
            return totalAmount * promotion.rate;
          }
          break;
          
        case 'full_reduction':
          if (promotion.reduce && totalAmount >= promotion.threshold) {
            return promotion.reduce;
          }
          break;
          
        case 'item_based':
          // 计算指定商品的优惠
          if (promotion.itemTypes && promotion.rate) {
            const applicableItems = this.cartItems.filter(item => 
              promotion.itemTypes.includes(item.itemType)
            );
            const applicableTotal = applicableItems.reduce((sum, item) => {
              const price = (item.unitPrice || 0) / 100;
              const quantity = item.quantity || 1;
              return sum + (price * quantity);
            }, 0);
            return applicableTotal * promotion.rate;
          }
          break;
      }
      return 0;
    },
    
    /**
     * 获取默认折扣率
     */
    getDefaultDiscountRate() {
      // 可以从配置或用户历史行为获取
      const userDiscountRate = this.getUserDiscountRate();
      if (userDiscountRate > 0) return userDiscountRate;
      
      // 默认：无折扣
      return 0;
    },
    
    /**
     * 根据用户历史获取折扣率
     */
    getUserDiscountRate() {
      // 这里可以调用用户API或读取本地历史
      const history = localStorage.getItem('user_order_history');
      if (history) {
        try {
          const orders = JSON.parse(history);
          if (orders.length > 0) {
            // 计算历史平均折扣率
            const avgRate = orders.reduce((sum, order) => {
              if (order.discount && order.total) {
                return sum + (order.discount / order.total);
              }
              return sum;
            }, 0) / orders.length;
            
            return Math.min(avgRate, 0.3); // 限制最大30%折扣
          }
        } catch (error) {
          console.warn('解析用户历史失败:', error);
        }
      }
      return 0;
    },
    
    /**
     * 根据商品类型估算折扣
     */
    estimateDiscountByItemType() {
      let totalDiscount = 0;
      
      this.cartItems.forEach(item => {
        const price = (item.unitPrice || 0) / 100;
        const quantity = item.quantity || 1;
        const itemTotal = price * quantity;
        
        // 根据商品类型给予不同折扣
        switch (item.itemType) {
          case 1: // 套餐
            // 套餐通常有较高折扣
            totalDiscount += itemTotal * 0.1; // 预估9折
            break;
          case 2: // 加油包
            // 加油包折扣较低
            totalDiscount += itemTotal * 0.05; // 预估95折
            break;
          default:
            totalDiscount += itemTotal * 0.03; // 默认97折
        }
      });
      
      return totalDiscount;
    },
    
    /**
     * 检查是否为新用户
     */
    isNewUser() {
      // 实际业务中需要调用API检查
      // 这里简化为检查localStorage
      const isNew = localStorage.getItem('is_new_user') === 'true';
      return isNew;
    },
    
    /**
     * 格式化价格（保留2位小数）
     */
    formatPrice(price) {
      if (typeof price !== 'number' || isNaN(price)) {
        return '0.00';
      }
      
      // 处理浮点数精度问题
      const rounded = Math.round(price * 100) / 100;
      return rounded.toFixed(2);
    },
    
    /**
     * 格式化价格（分转元）
     */
    formatPriceYuan(cents) {
      if (typeof cents !== 'number' || isNaN(cents)) {
        return '0.00';
      }
      
      const yuan = cents / 100;
      return this.formatPrice(yuan);
    },
    
    /**
     * 比较前后端价格差异
     */
    compareWithBackendPrice(frontendResult, backendResult) {
      if (!backendResult || !frontendResult) {
        return {
          matched: false,
          diff: null,
          message: '无法比较：缺少价格数据'
        };
      }
      
      const frontendFinal = parseFloat(frontendResult.final);
      const backendFinal = parseFloat(backendResult.payableAmount || backendResult.final);
      
      if (isNaN(frontendFinal) || isNaN(backendFinal)) {
        return {
          matched: false,
          diff: null,
          message: '价格数据格式错误'
        };
      }
      
      const diff = Math.abs(frontendFinal - backendFinal);
      const diffPercent = frontendFinal > 0 ? (diff / frontendFinal * 100) : 0;
      
      // 允许的误差范围：1分钱或1%
      const tolerance = 0.01;
      const tolerancePercent = 1;
      
      const matched = diff <= tolerance || diffPercent <= tolerancePercent;
      console("diffPercent", diffPercent)
      return {
        matched,
        diff,
        diffPercent: diffPercent.toFixed(2),
        frontend: frontendFinal,
        backend: backendFinal,
        message: matched ? '价格匹配' : `价格差异：${diff.toFixed(2)}元 (${diffPercent.toFixed(2)}%)`
      };
    },
    /**
     * 清空购物车（支付成功后调用）
     */
    async clearShoppingCart() {
      try {
        // 调用清空购物车接口
        await clearCart();
        
        // 同时清空本地状态
        this.cartItems = [];
        this.$store.dispatch('cart/clearCart');
        
        console.info("购物车已清空");
      } catch (error) {
        console.warn("清空购物车失败:", error);
        // 不阻断主流程，记录日志即可
      }
    },
    /**
     * 支付成功处理
     */
    async onPaymentSuccess(orderNo) {
      try {
        this.orderData.paymentStatus = 'paid';
        await this.clearShoppingCart();

        // 🔥 直接整页跳转，替换 $router.push
        // 整页刷新会清空所有 DOM，包括残留的弹框
        window.location.href = `/admin/order/success?orderNo=${encodeURIComponent(orderNo)}`;

        console.info("支付成功处理完成:", orderNo);
      } catch (error) {
        console.error("支付成功处理失败:", error);
        window.location.href = `/admin/order/success?orderNo=${encodeURIComponent(orderNo)}&warning=1`;
      }
    },
    
    /**
     * 支付失败处理
     */
    onPaymentFailure(error) {
      console.error("支付失败:", error);
      
      // 跳转到失败页面
      this.$router.push({
        path: '/order/fail',
        query: { 
          orderNo: this.orderData.orderNo,
          error: error.message
        }
      });
    },   

// 处理重新购买流程
async handleRenewOrder(orderNo) {
  try {
    console.log("处理重新购买，订单号:", orderNo);
    
    // 1. 获取订单详情
    const response = await getOrderDetail(orderNo);
    if (response.code !== 200) {
      this.$message.error('获取订单详情失败');
      this.$router.back();
      return;
    }
    
    const order = response.data;
    console.log("订单详情:", order);
    
    // 2. 验证订单状态（前端验证）
    if (order.order_status !== 2) {
      this.$message.warning('订单不是已取消状态');
      this.$router.back();
      return;
    }
    
    const cancelReason = order.cancelReason || order.cancel_reason;
    if (!cancelReason || !cancelReason.includes('支付超时')) {
      this.$message.warning('只有支付超时的订单可以重新购买');
      this.$router.back();
      return;
    }
    
    // 3. 关键：将订单项转换为购物车格式
    const orderItems = order.items || [];
    this.cartItems = this.convertOrderItemsToCartItems(orderItems);
    
    console.log("转换后的购物车数据:", this.cartItems);
    
    // 4. 显示订单信息（使用原订单数据）
    this.orderData = {
      orderNo: order.orderNo || order.order_no,
      originalTotal: order.totalAmount || order.total_amount,
      discountAmount: order.discountAmount || order.discount_amount,
      payableAmount: order.payableAmount || order.payable_amount,
      itemDetails: orderItems,
      paymentMethod: order.paymentMethod || 'alipay',
      isRenewOrder: true  // 标记为重新购买
    };
    
    // 5. 设置页面标题和提示
    this.pageTitle = '重新购买订单';
    this.showRenewNotice = true;
    
    // 6. 设置金额显示字段
    this.backendTotal = Number(order.totalAmount || order.total_amount || 0);
    this.backendDiscount = Number(order.discountAmount || order.discount_amount || 0);
    this.backendFinal = Number(order.payableAmount || order.payable_amount || 0);
    
    console.log("重新购买订单数据设置完成:", {
      orderData: this.orderData,
      cartItems: this.cartItems,
      backendFinal: this.backendFinal
    });
    
  } catch (error) {
    console.error('处理重新购买失败:', error);
    this.$message.error('处理失败: ' + (error.message || '未知错误'));
    this.$router.back();
  }
},

        // 转换OrderItem到购物车格式
        convertOrderItemsToCartItems(orderItems) {
          if (!orderItems || orderItems.length === 0) {
            console.warn("订单项为空");
            return [];
          }
          
          console.log("原始订单项:", orderItems);
          
          const cartItems = orderItems.map(item => {
            // 处理多种可能的字段名格式
            const itemId = item.item_id || item.itemId;
            const itemType = item.item_type || item.itemType;
            const itemName = item.item_name || item.itemName;
            const unitPrice = item.unit_price || item.unitPrice;
            const quantity = item.quantity || 1;
            
            // 构建购物车项格式（根据你的createOrder接口要求）
            const cartItem = {
              itemId: itemId,
              itemType: itemType,
              quantity: quantity,
              // 可选字段，用于调试和显示
              itemName: itemName,
              unitPrice: unitPrice,
              // 注意：如果后端create接口需要其他字段，请添加
              id: item.id || Date.now() + Math.random() // 临时ID，确保有唯一标识
            };
            
            console.log("转换单条:", item, "→", cartItem);
            return cartItem;
          });
          
          console.log("转换结果:", cartItems);
          return cartItems;
        }, 

        // 处理继续支付
        async handleContinuePayment(orderNo) {
          try {
            console.log("处理继续支付，订单号:", orderNo);
            
            // 1. 获取订单详情
            const response = await getOrderDetail(orderNo);
            if (response.code !== 200) {
              this.$message.error('获取订单详情失败');
              this.$router.back();
              return;
            }
            
            const order = response.data;
            console.log("订单详情:", order);
            
            // 2. 验证订单状态
            if (order.paymentStatus !== 0) {
              this.$message.warning('订单不是待支付状态');
              this.$router.back();
              return;
            }
            
            // 检查是否过期（15分钟）
            const createTime = order.createdAt || order.created_at;
            if (createTime) {
              const expireTime = new Date(createTime).getTime() + 15 * 60 * 1000;
              if (Date.now() >= expireTime) {
                this.$message.warning('订单已过期，无法继续支付');
                this.$router.back();
                return;
              }
            }
            
            // 3. 显示订单信息
            this.orderData = {
              orderNo: order.orderNo || order.order_no,
              originalTotal: order.totalAmount || order.total_amount,
              discountAmount: order.discountAmount || order.discount_amount,
              payableAmount: order.payableAmount || order.payable_amount,
              itemDetails: order.items || [],
              paymentMethod: order.paymentMethod || 'alipay',
              isContinuePayment: true  // 标记为继续支付
            };
            
            // 4. 设置页面标题
            this.pageTitle = '继续支付订单';
            
            console.log("继续支付订单数据设置完成:", this.orderData);
            
          } catch (error) {
            console.error('处理继续支付失败:', error);
            this.$message.error('处理失败: ' + (error.message || '未知错误'));
            this.$router.back();
          }
        },    

        // 正常购物车支付流程
        async handleNormalCheckout() {
          console.log("正常购物车支付流程");
          
          // 1. 获取购物车数据
          const cartData = this.$route.query.cartData 
            ? JSON.parse(this.$route.query.cartData)
            : await this.loadCartData();
          
          this.cartItems = cartData.items;
          console.log("购物车商品:", this.cartItems);
          
          // 2. 先使用前端临时价格展示
          this.orderData.frontendTotal = this.cartItems.reduce((sum, item) => 
            sum + (item.unitPrice * item.quantity), 0);
          this.orderData.frontendDiscount = cartData.frontendDiscount || 0;
          this.orderData.frontendFinal = this.orderData.frontendTotal - this.orderData.frontendDiscount;
          
          // 3. 调用后端获取准确价格
          await this.createOrderAndGetRealPrice();  
          
          // 4. 生成订单号（或使用后端返回的订单号）
          if (!this.backendOrderNo) {
            this.generateOrderNo();
          }
        },
        // 检查订单是否可以支付
        checkOrderCanPay(order) {
          // 支付状态必须是0（待支付）
          if (order.paymentStatus !== 0) return false;
          
          // 订单状态必须是0（待处理）
          if (order.orderStatus !== 0) return false;
          
          // 检查是否过期（15分钟）
          const createTime = order.createdAt || order.created_at;
          if (!createTime) return false;
          
          const expireTime = new Date(createTime).getTime() + 15 * 60 * 1000;
          return Date.now() < expireTime;
        },
        // 购物车支付流程（保持原有逻辑）
        async handleCartCheckout() {
          console.log("正常购物车支付流程");
          
          // 1. 获取购物车数据
          const cartData = this.$route.query.cartData 
            ? JSON.parse(this.$route.query.cartData)
            : await this.loadCartData();
          
          this.cartItems = cartData.items;
          console.log("购物车商品:", this.cartItems);
          
          // 2. 先使用前端临时价格展示
          this.orderData.frontendTotal = this.cartItems.reduce((sum, item) => 
            sum + (item.unitPrice * item.quantity), 0);
          this.orderData.frontendDiscount = cartData.frontendDiscount || 0;
          this.orderData.frontendFinal = this.orderData.frontendTotal - this.orderData.frontendDiscount;
          
          // 3. 调用后端获取准确价格
          await this.createOrderAndGetRealPrice();  
          
          // 4. 设置页面标题
          this.pageTitle = '确认订单';
        },
        
        extractOrderNoFromForm(htmlString) {
          console.log("extractOrderNoFromForm", htmlString)
            try {
                // 方法1: 使用正则表达式匹配 out_trade_no 的值
                const regex = /out_trade_no\\":\\"([A-Z0-9]+)"/;
                const match = htmlString.match(regex);
                if (match && match[1]) {
                    return match[1]; // 返回 DD1769765967864871815
                }
                
                // 方法2: 更稳健的方法 - 解析整个biz_content的JSON
                const bizContentRegex = /biz_content\\" value=\\"([^"]+)"/;
                const bizContentMatch = htmlString.match(bizContentRegex);
                if (bizContentMatch && bizContentMatch[1]) {
                    // 解码HTML实体并解析JSON
                    const decoded = bizContentMatch[1].replace(/\\"/g, '"');
                    const bizContent = JSON.parse(decoded);
                    return bizContent.out_trade_no;
                }
            } catch (error) {
                console.error("解析订单号失败:", error);
            }
            return null;
        },
        async handleWechatPayment(orderNo, paymentData) {
            try {
                console.log("微信支付数据:", paymentData);
                
                // 根据交易类型处理
                const tradeType = paymentData.tradeType || 'NATIVE';
                
                if (tradeType === 'NATIVE') {
                    // 扫码支付 - 显示二维码
                    this.showWechatQRCode(paymentData.codeUrl || paymentData.payUrl, orderNo);
                } else if (tradeType === 'JSAPI') {
                    // 公众号支付
                    if (paymentData.jsApiParams) {
                        this.invokeWechatJSAPI(paymentData.jsApiParams);
                    } else {
                        this.$message.error('未获取到JSAPI支付参数');
                    }
                } else if (tradeType === 'MWEB') {
                    // H5支付 - 跳转
                    const payUrl = paymentData.mwebUrl || paymentData.payUrl;
                    if (payUrl) {
                        window.open(payUrl, '_blank');
                    } else {
                        this.$message.error('未获取到支付链接');
                    }
                } else {
                    this.$message.error(`不支持的微信支付类型: ${tradeType}`);
                }
            } catch (error) {
                console.error('微信支付处理失败:', error);
                this.$message.error('微信支付处理失败: ' + error.message);
            }
        },
  
        // 微信支付入口（只处理支付数据，不重复下单）
        // 这个方法现在不会被 handlePayment 自动调用，但保留它作为可复用入口（比如以后别的地方想直接复用）。如果不需要，也可以直接删掉。
        async payWithWechat(paymentData) {
          this.loading = true;
          try {
            console.log("=== 开始微信支付流程 ===");

            if (!paymentData) {
              throw new Error('缺少微信支付数据');
            }

            const orderNo = paymentData.orderNo || this.orderData.orderNo || '临时订单号';
            await this.handleWechatPayment(orderNo, paymentData);

          } catch (error) {
            console.error('微信支付失败:', error);
            this.$message.error('微信支付失败: ' + error.message);
          } finally {
            this.loading = false;
          }
        },


      // 添加微信支付轮询方法：
      startWechatPaymentPolling(orderNo) {
          const pollInterval = setInterval(async () => {
              try {
                  const paid = await this.checkWechatPaymentStatus(orderNo);
                  if (paid) {
                      clearInterval(pollInterval);
                      await this.onPaymentSuccess(orderNo);
                      this.$message.success('微信支付成功！');
                      // 关闭二维码弹窗
        clearInterval(pollInterval);

        // 🔥 关键：先关闭微信支付弹框
        if (this.wechatMsgBoxInstance) {
          this.wechatMsgBoxInstance.close();
          this.wechatMsgBoxInstance = null;
        }
                // 再跳转成功页
        await this.onPaymentSuccess(orderNo);
        this.$message.success('微信支付成功！');
                  }
              } catch (error) {
                  console.error('轮询微信支付状态失败:', error);
              }
          }, 3000); // 每3秒检查一次
          
          // 10分钟后停止轮询
          setTimeout(() => {
              clearInterval(pollInterval);
              this.$message.warning('支付超时，请重新支付');
          }, 10 * 60 * 1000);
      },

      // 添加检查微信支付状态的方法：
async checkWechatPaymentStatus(orderNo) {
  try {
    const response = await http.get(`/aics/order/wechatpay/query/${orderNo}`);  
    console.log("checkWechatPaymentStatus", response);
    if (response.data.code === 200) {
      const result = response.data.data;
      const tradeState = result.trade_state || result.trade_state_desc;
      return tradeState === 'SUCCESS' || tradeState === '支付成功';
    }
    return false;
  } catch (error) {
    console.error('检查微信支付状态失败:', error);
    return false;
  }
},
  }      
}
</script>

<style scoped>
.checkout-container {
  padding: 20px;
}

.order-card, .payment-card {
  margin-bottom: 20px;
}

.order-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid #ebeef5;
}

.order-no {
  color: #666;
  font-size: 14px;
}

.order-items {
  margin-bottom: 20px;
}

.order-item {
  display: flex;
  justify-content: space-between;
  padding: 10px 0;
  border-bottom: 1px dashed #f0f0f0;
}

.order-item:last-child {
  border-bottom: none;
}

.item-name {
  flex: 1;
}

.item-quantity {
  margin: 0 15px;
}

.item-price {
  font-weight: 500;
  min-width: 80px;
  text-align: right;
}

.order-summary {
  padding: 15px;
  background-color: #f9f9f9;
  border-radius: 4px;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
  font-size: 14px;
}

.summary-row.total {
  font-size: 16px;
  font-weight: 600;
  color: #e4393c;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid #e0e0e0;
}

.payment-methods {
  margin: 20px 0;
}

.payment-method {
  display: flex;
  align-items: center;
  padding: 15px;
  margin-bottom: 10px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s;
}

.payment-method:hover {
  border-color: #409eff;
  background-color: #f5f7fa;
}

.payment-method.selected {
  border-color: #409eff;
  background-color: #ecf5ff;
}

.method-icon {
  margin-right: 15px;
  font-size: 24px;
  color: #409eff;
}

.method-info {
  flex: 1;
}

.method-name {
  display: block;
  font-weight: 500;
  margin-bottom: 5px;
}

.method-desc {
  font-size: 12px;
  color: #999;
}

.method-radio {
  color: #409eff;
  font-size: 20px;
}

.payment-actions {
  display: flex;
  justify-content: flex-end;
  gap: 15px;
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid #ebeef5;
}
.tips{
  font-family: 微软雅黑;
  font-size:  small;
  color: red;
}
</style>